/**
 * TUVI - Cronograma General
 * Dashboard que muestra el cronograma de todos los proyectos en una vista unificada
 * con timeline visual, estados, progreso y actividad actual de cada proyecto
 */

import React, { useState, useEffect, useMemo } from 'react';
import NombreSemanaEditable from './NombreSemanaEditable';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import client from '../../services/graphqlClient';
import { BACKEND_URL } from '../../config';
import { semaforoCronograma } from '../../utils/semaforoCronograma';
import ModalEvidenciasCronograma from './Detalle/ModalEvidenciasCronograma';
import ModalInformeAvance from './Detalle/ModalInformeAvance';
import './CronogramaGeneral.css';
import './Detalle/CronogramaProyecto.css';

const SIN_RESIDENTE = '__sin_residente__';

const CronogramaGeneral =({ onModuleChange, onVerDetalle }) => {
  const { success, error } = useNotification();
  const { user } = useAuth();
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  // Filtro por residente asignado: '' = todos, SIN_RESIDENTE = proyectos sin residente
  const [filtroResidente, setFiltroResidente] = useState('');
  // Sub-filtro por nivel del semáforo (solo aplica dentro de "Retrasados")
  const [filtroNivel, setFiltroNivel] = useState(null); // null | 'verde' | 'amarillo' | 'rojo'
  const [busqueda, setBusqueda] = useState('');
  const [vistaExpandida, setVistaExpandida] = useState(null);
  const [generandoMasivo, setGenerandoMasivo] = useState(false);
  const [evidenciaActividad, setEvidenciaActividad] = useState(null); // actividad para modal evidencias
  const [informeProyectoId, setInformeProyectoId] = useState(null);   // proyecto para modal informe

  useEffect(() => {
    cargarDatos();
  }, []);

  // Tras renombrar una semana se refleja al instante, sin recargar toda la vista.
  const recargarTrasRenombrar = (itemId, nuevoNombre) => {
    setProyectos(curr => curr.map(p => ({
      ...p,
      actividades: (p.actividades || []).map(a => a.id === itemId ? { ...a, nombre: nuevoNombre } : a),
      cronograma: (p.cronograma || []).map(a => a.id === itemId ? { ...a, nombre: nuevoNombre } : a),
    })));
  };

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const data = await client.query(`{ cronogramaGeneral { proyecto_id proyecto_nombre apartamento nombre_cliente estado plazo_ejecucion residente items { id orden nombre etiqueta_semana fecha_inicio fecha_fin fecha_inicio_real fecha_fin_real duracion_dias es_hito estado progreso tareas { id orden descripcion tipo estado progreso fecha_completado usuario_id usuario_nombre notas } } } }`);
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const proys = (data.cronogramaGeneral || []).map(p => {
        const items = p.items || [];
        const completadas = items.filter(i => i.fecha_fin_real).length;
        const enProgreso = items.filter(i => i.fecha_inicio_real && !i.fecha_fin_real).length;
        const retrasadas = items.filter(i => !i.fecha_fin_real && !i.fecha_inicio_real && i.fecha_inicio && new Date(i.fecha_inicio + 'T00:00:00') < hoy && !i.es_hito && i.duracion_dias > 0).length;
        const pendientes = items.length - completadas - enProgreso;
        // % del proyecto: si hay % cargados por actividad → promedio ponderado por
        // duración; si no, estimación por días (comportamiento anterior).
        const hayProg = items.some(i => (i.progreso || 0) > 0);
        let progreso;
        if (hayProg) {
          const conDur = items.filter(i => (i.duracion_dias || 0) > 0);
          if (conDur.length > 0) {
            const peso = conDur.reduce((s, i) => s + i.duracion_dias, 0);
            const acum = conDur.reduce((s, i) => s + (i.progreso || 0) * i.duracion_dias, 0);
            progreso = Math.min(100, Math.round(acum / peso));
          } else {
            const noHito = items.filter(i => !i.es_hito);
            progreso = noHito.length ? Math.round(noHito.reduce((s, i) => s + (i.progreso || 0), 0) / noHito.length) : 0;
          }
        } else {
          const diasTotal = items.reduce((s, i) => s + (i.duracion_dias || 0), 0);
          let diasAvance = 0;
          items.forEach(i => {
            if (i.fecha_fin_real) diasAvance += (i.duracion_dias || 0);
            else if (i.fecha_inicio_real) {
              const ini = new Date(i.fecha_inicio_real + 'T00:00:00');
              const parcial = Math.max(0, Math.floor((hoy - ini) / 86400000));
              diasAvance += Math.min(parcial, i.duracion_dias || 0);
            }
          });
          progreso = diasTotal > 0 ? Math.min(100, Math.round((diasAvance / diasTotal) * 100)) : 0;
        }
        const fechaInicio = items.length > 0 ? items[0].fecha_inicio : null;
        const fechaFin = items.length > 0 ? items[items.length - 1].fecha_fin : null;
        const diasRestantes = fechaFin ? Math.floor((new Date(fechaFin + 'T00:00:00') - hoy) / 86400000) : 0;
        const actActual = items.find(i => i.fecha_inicio_real && !i.fecha_fin_real);
        let estadoCron = 'no_iniciado';
        if (completadas === items.length && items.length > 0) estadoCron = 'completado';
        else if (retrasadas > 0) estadoCron = 'retrasado';
        else if (enProgreso > 0 || completadas > 0) estadoCron = 'en_progreso';
        return {
          ...p, id: p.proyecto_id, cliente: p.nombre_cliente,
          cronograma: items, actividades: items,
          total_actividades: items.length, completadas, en_progreso: enProgreso,
          retrasadas, pendientes, progreso, estado_cronograma: estadoCron,
          fecha_inicio: fechaInicio, fecha_fin: fechaFin, dias_restantes: diasRestantes,
          actividad_actual: actActual || null,
        };
      });
      setProyectos(proys);
    } catch (err) {
      console.error('Error cargando cronogramas:', err);
      error('Error al cargar los cronogramas');
    } finally {
      setLoading(false);
    }
  };

  // Marcar/desmarcar subtarea del cronograma. Sincrónico con CronogramaProyecto.jsx
  // porque ambas vistas leen/escriben la misma tabla en BD.
  // Actualizacion optimista local: no se recarga la pagina al cambiar el check/N/A.
  const handleMarcarTarea = async (tareaId, estado) => {
    // 1) Snapshot previo por si hay que revertir
    let prev = null;
    const ahoraIso = new Date().toISOString();
    const userNombre = user?.nombre_completo || user?.username || null;

    const hoyIso = ahoraIso.slice(0, 10);

    setProyectos(curr => curr.map(p => {
      const cron = p.cronograma || [];
      let actUpdated = false;
      const nuevoCron = cron.map(item => {
        if (!item.tareas) return item;
        const idx = item.tareas.findIndex(t => t.id === tareaId);
        if (idx === -1) return item;
        prev = {
          proyId: p.id, itemId: item.id, idx, tarea: item.tareas[idx],
          fecha_inicio_real: item.fecha_inicio_real,
          fecha_fin_real: item.fecha_fin_real,
          estado: item.estado,
        };
        const nuevasTareas = [...item.tareas];
        nuevasTareas[idx] = {
          ...nuevasTareas[idx],
          estado,
          fecha_completado: estado === 'completado' ? ahoraIso : null,
          usuario_nombre: estado === 'completado' ? userNombre : nuevasTareas[idx].usuario_nombre,
        };
        actUpdated = true;

        // Reflejar el auto-llenado del backend: fecha_inicio_real al primer ítem decidido,
        // fecha_fin_real cuando todos los ítems están decididos. No sobrescribe fechas existentes.
        const decididas = nuevasTareas.filter(t => t.estado !== 'pendiente').length;
        const algunaDecidida = decididas > 0;
        const todasDecididas = decididas === nuevasTareas.length;
        const nuevoEstado = todasDecididas ? 'completado' : (algunaDecidida ? 'en_progreso' : 'pendiente');

        return {
          ...item,
          tareas: nuevasTareas,
          fecha_inicio_real: (algunaDecidida && !item.fecha_inicio_real) ? hoyIso : item.fecha_inicio_real,
          fecha_fin_real:    (todasDecididas && !item.fecha_fin_real)    ? hoyIso : item.fecha_fin_real,
          estado: nuevoEstado,
        };
      });
      if (!actUpdated) return p;
      return { ...p, cronograma: nuevoCron, actividades: nuevoCron };
    }));

    // 2) Disparar mutation en background
    try {
      const r = await client.mutate(
        `mutation($id: Int!, $estado: String!) { marcarTareaCronograma(id: $id, estado: $estado) { success message } }`,
        { id: tareaId, estado }
      );
      if (!r.marcarTareaCronograma.success) {
        throw new Error(r.marcarTareaCronograma.message || 'No se pudo actualizar la tarea');
      }
    } catch (err) {
      // 3) Revertir si fallo (también las fechas/estado del padre)
      if (prev) {
        setProyectos(curr => curr.map(p => {
          if (p.id !== prev.proyId) return p;
          const nuevoCron = (p.cronograma || []).map(item => {
            if (item.id !== prev.itemId || !item.tareas) return item;
            const nuevasTareas = [...item.tareas];
            nuevasTareas[prev.idx] = prev.tarea;
            return {
              ...item,
              tareas: nuevasTareas,
              fecha_inicio_real: prev.fecha_inicio_real,
              fecha_fin_real: prev.fecha_fin_real,
              estado: prev.estado,
            };
          });
          return { ...p, cronograma: nuevoCron, actividades: nuevoCron };
        }));
      }
      error('Error al actualizar tarea: ' + (err.message || ''));
    }
  };

  const generarCronogramasMasivos = async () => {
    try {
      setGenerandoMasivo(true);
      // TODO: Implementar generación masiva via GraphQL si se necesita
      error('Función temporalmente deshabilitada');
    } catch (err) {
      error('Error al generar cronogramas');
    } finally {
      setGenerandoMasivo(false);
    }
  };

  // Residentes para el filtro (los que tienen proyectos en esta vista)
  const residentes = useMemo(() => {
    const nombres = [...new Set(proyectos.map(p => p.residente).filter(Boolean))];
    return nombres.sort((a, b) => a.localeCompare(b, 'es'));
  }, [proyectos]);
  const haySinResidente = useMemo(() => proyectos.some(p => !p.residente), [proyectos]);

  // Proyectos del residente elegido: de aquí salen los totales, el desglose y la lista,
  // para que al filtrar por residente los números sean los suyos.
  const proyectosResidente = useMemo(() => {
    if (!filtroResidente) return proyectos;
    if (filtroResidente === SIN_RESIDENTE) return proyectos.filter(p => !p.residente);
    return proyectos.filter(p => p.residente === filtroResidente);
  }, [proyectos, filtroResidente]);

  const resumen = useMemo(() => {
    if (loading && proyectos.length === 0) return null;
    const base = proyectosResidente;
    return {
      total_proyectos: base.length,
      en_progreso: base.filter(p => p.estado_cronograma === 'en_progreso').length,
      completados: base.filter(p => p.estado_cronograma === 'completado').length,
      retrasados: base.filter(p => p.estado_cronograma === 'retrasado').length,
      no_iniciados: base.filter(p => p.estado_cronograma === 'no_iniciado').length,
    };
  }, [loading, proyectos, proyectosResidente]);

  // Desglose por semáforo de los proyectos RETRASADOS (verde/naranja/rojo).
  // El semáforo mide los días de obra transcurridos desde el inicio del cronograma.
  const desgloseRetrasados = useMemo(() => {
    const retrasados = proyectosResidente.filter(p => p.estado_cronograma === 'retrasado');
    const cuenta = { verde: 0, amarillo: 0, rojo: 0, sin: 0 };
    retrasados.forEach(p => {
      const nivel = semaforoCronograma(p.fecha_inicio)?.nivel;
      if (nivel) cuenta[nivel] += 1; else cuenta.sin += 1;
    });
    const total = retrasados.length || 0;
    const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);
    return [
      { nivel: 'verde',    label: 'En tiempo', detalle: 'Menos de 45 días de obra', n: cuenta.verde,    pct: pct(cuenta.verde) },
      { nivel: 'amarillo', label: 'Alerta',    detalle: '45 días o más de obra',    n: cuenta.amarillo, pct: pct(cuenta.amarillo) },
      { nivel: 'rojo',     label: 'Crítico',   detalle: '60 días o más de obra',    n: cuenta.rojo,     pct: pct(cuenta.rojo) },
    ];
  }, [proyectosResidente]);

  const proyectosFiltrados = useMemo(() => {
    let resultado = [...proyectosResidente];

    if (filtroEstado !== 'todos') {
      resultado = resultado.filter(p => p.estado_cronograma === filtroEstado);
    }

    // Dentro de "Retrasados", se puede afinar por nivel del semáforo.
    if (filtroEstado === 'retrasado' && filtroNivel) {
      resultado = resultado.filter(p => semaforoCronograma(p.fecha_inicio)?.nivel === filtroNivel);
    }

    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      resultado = resultado.filter(p =>
        p.proyecto_nombre?.toLowerCase().includes(q) ||
        p.cliente?.toLowerCase().includes(q) ||
        p.apartamento?.toLowerCase().includes(q) ||
        p.residente?.toLowerCase().includes(q)
      );
    }

    return resultado;
  }, [proyectosResidente, filtroEstado, filtroNivel, busqueda]);

  const formatFecha = (fecha) => {
    if (!fecha) return '—';
    const d = new Date(fecha + 'T12:00:00');
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Exportar a Excel con los datos principales de cada proyecto (respeta el filtro/búsqueda)
  const exportarExcel = async () => {
    if (proyectosFiltrados.length === 0) { error('No hay proyectos para exportar'); return; }
    const XLSX = await import('xlsx-js-style');
    const RGB_SEM = { verde: '16A34A', amarillo: 'D97706', rojo: 'DC2626' };

    const headers = ['#', 'Proyecto', 'Apartamento', 'Cliente', 'Residente', 'Estado', 'Progreso', 'Días de obra',
      'Inicio', 'Entrega', 'Días restantes', 'Actividades', 'Actividad actual'];
    const filas = proyectosFiltrados.map((p, i) => {
      const sem = semaforoCronograma(p.fecha_inicio);
      const restante = p.estado_cronograma === 'completado' ? 'Completado'
        : p.dias_restantes >= 0 ? `${p.dias_restantes} restantes` : `${Math.abs(p.dias_restantes)} de retraso`;
      return [
        i + 1,
        p.proyecto_nombre || '-',
        p.apartamento || '-',
        p.cliente || '-',
        p.residente || 'Sin asignar',
        getEstadoConfig(p.estado_cronograma).label,
        (p.progreso || 0) / 100,
        sem ? sem.dias : '',
        p.fecha_inicio ? formatFecha(p.fecha_inicio) : '-',
        p.fecha_fin ? formatFecha(p.fecha_fin) : '-',
        restante,
        `${p.completadas}/${p.total_actividades}`,
        p.actividad_actual?.nombre || '-',
      ];
    });

    const bordeThin = { style: 'thin', color: { rgb: 'E2E8F0' } };
    const borde = { top: bordeThin, bottom: bordeThin, left: bordeThin, right: bordeThin };
    const styleHeader = {
      font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '1C2837' } },
      alignment: { vertical: 'center', horizontal: 'center', wrapText: true },
      border: borde,
    };
    const styleCell = (align = 'left', extra = {}) => ({
      font: { name: 'Calibri', sz: 10, color: { rgb: '1E293B' } },
      alignment: { vertical: 'center', horizontal: align, wrapText: align === 'left' },
      border: borde, ...extra,
    });
    const styleSem = (rgb) => ({
      font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb } },
      alignment: { vertical: 'center', horizontal: 'center' }, border: borde,
    });

    const fechaHoy = new Date().toLocaleDateString('es-CO');
    const textoResidente = !filtroResidente ? ''
      : ` · Residente: ${filtroResidente === SIN_RESIDENTE ? 'sin asignar' : filtroResidente}`;
    const aoa = [
      ['CRONOGRAMA GENERAL — AVANCE DE PROYECTOS'],
      [`Generado ${fechaHoy} · ${proyectosFiltrados.length} proyecto(s)${textoResidente}`],
      [],
      headers,
      ...filas,
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } },
    ];
    ws['!cols'] = [{ wch: 5 }, { wch: 28 }, { wch: 14 }, { wch: 26 }, { wch: 24 }, { wch: 13 }, { wch: 10 }, { wch: 12 }, { wch: 13 }, { wch: 13 }, { wch: 15 }, { wch: 12 }, { wch: 34 }];
    ws['!rows'] = [{ hpt: 26 }, { hpt: 18 }, undefined, { hpt: 30 }];
    ws['!freeze'] = { xSplit: 0, ySplit: 4 };
    ws['!autofilter'] = { ref: `A4:${XLSX.utils.encode_col(headers.length - 1)}${4 + filas.length}` };

    ws['A1'].s = { font: { name: 'Calibri', sz: 13, bold: true, color: { rgb: '1C2837' } }, alignment: { horizontal: 'center' } };
    ws['A2'].s = { font: { name: 'Calibri', sz: 9, italic: true, color: { rgb: '64748B' } }, alignment: { horizontal: 'center' } };
    for (let c = 0; c < headers.length; c++) ws[XLSX.utils.encode_cell({ r: 3, c })].s = styleHeader;
    for (let i = 0; i < proyectosFiltrados.length; i++) {
      const r = 4 + i;
      const sem = semaforoCronograma(proyectosFiltrados[i].fecha_inicio);
      ws[XLSX.utils.encode_cell({ r, c: 0 })].s = styleCell('center');
      ws[XLSX.utils.encode_cell({ r, c: 1 })].s = styleCell('left', { font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: '1E293B' } } });
      ws[XLSX.utils.encode_cell({ r, c: 2 })].s = styleCell('center');
      ws[XLSX.utils.encode_cell({ r, c: 3 })].s = styleCell('left');
      ws[XLSX.utils.encode_cell({ r, c: 4 })].s = proyectosFiltrados[i].residente
        ? styleCell('left')
        : styleCell('left', { font: { name: 'Calibri', sz: 10, italic: true, color: { rgb: '94A3B8' } } });
      ws[XLSX.utils.encode_cell({ r, c: 5 })].s = styleCell('center');
      const cPct = ws[XLSX.utils.encode_cell({ r, c: 6 })]; cPct.s = styleCell('center', { numFmt: '0%' }); cPct.z = '0%';
      const cSem = ws[XLSX.utils.encode_cell({ r, c: 7 })];
      cSem.s = sem ? styleSem(RGB_SEM[sem.nivel]) : styleCell('center');
      ws[XLSX.utils.encode_cell({ r, c: 8 })].s = styleCell('center');
      ws[XLSX.utils.encode_cell({ r, c: 9 })].s = styleCell('center');
      ws[XLSX.utils.encode_cell({ r, c: 10 })].s = styleCell('center');
      ws[XLSX.utils.encode_cell({ r, c: 11 })].s = styleCell('center');
      ws[XLSX.utils.encode_cell({ r, c: 12 })].s = styleCell('left');
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cronograma General');
    XLSX.writeFile(wb, `Cronograma_General_TUVI_${new Date().toISOString().slice(0, 10)}.xlsx`);
    success('Excel exportado');
  };

  const getEstadoConfig = (estado) => {
    const config = {
      'no_iniciado': { label: 'No iniciado', color: '#9ca3af', bg: '#f3f4f6', icon: '⏳' },
      'en_progreso': { label: 'En progreso', color: '#3b82f6', bg: '#eff6ff', icon: '🔨' },
      'completado': { label: 'Completado', color: '#10b981', bg: '#ecfdf5', icon: '✅' },
      'retrasado': { label: 'Retrasado', color: '#ef4444', bg: '#fef2f2', icon: '⚠️' }
    };
    return config[estado] || config['no_iniciado'];
  };

  if (loading) {
    return (
      <div className="cg-container">
        <div className="cg-loading">
          <div className="cg-spinner"></div>
          <p>Cargando cronogramas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cg-container">
      {/* Header */}
      <div className="cg-header">
        <div className="cg-header-left">
          <h2 className="cg-title">Cronograma General</h2>
          <p className="cg-subtitle">Vista global del avance de todos los proyectos</p>
        </div>
        <div className="cg-header-actions">
          <button className="cg-btn cg-btn-excel" onClick={exportarExcel} disabled={proyectosFiltrados.length === 0}>
            📊 Exportar Excel
          </button>
          <button className="cg-btn cg-btn-outline" onClick={cargarDatos}>
            Actualizar
          </button>
        </div>
      </div>

      {/* Resumen Global */}
      {resumen && (
        <div className="cg-resumen">
          <div className="cg-resumen-card cg-resumen-total">
            <span className="cg-resumen-numero">{resumen.total_proyectos}</span>
            <span className="cg-resumen-label">Total</span>
          </div>
          <div className="cg-resumen-card cg-resumen-progreso" onClick={() => setFiltroEstado(filtroEstado === 'en_progreso' ? 'todos' : 'en_progreso')}>
            <span className="cg-resumen-numero">{resumen.en_progreso}</span>
            <span className="cg-resumen-label">En progreso</span>
          </div>
          <div className="cg-resumen-card cg-resumen-completado" onClick={() => setFiltroEstado(filtroEstado === 'completado' ? 'todos' : 'completado')}>
            <span className="cg-resumen-numero">{resumen.completados}</span>
            <span className="cg-resumen-label">Completados</span>
          </div>
          <div
            className={`cg-resumen-card cg-resumen-retrasado${filtroEstado === 'retrasado' ? ' cg-card-activa' : ''}`}
            onClick={() => {
              const apagar = filtroEstado === 'retrasado';
              setFiltroEstado(apagar ? 'todos' : 'retrasado');
              if (apagar) setFiltroNivel(null);
            }}
          >
            <span className="cg-resumen-numero">{resumen.retrasados}</span>
            <span className="cg-resumen-label">Retrasados</span>
          </div>
          <div className="cg-resumen-card cg-resumen-noiniciado" onClick={() => setFiltroEstado(filtroEstado === 'no_iniciado' ? 'todos' : 'no_iniciado')}>
            <span className="cg-resumen-numero">{resumen.no_iniciados}</span>
            <span className="cg-resumen-label">No iniciados</span>
          </div>
        </div>
      )}

      {/* Desglose del semáforo: solo al seleccionar "Retrasados" */}
      {resumen && filtroEstado === 'retrasado' && (
        <div className="cg-desglose">
          <div className="cg-desglose-titulo">
            <strong>{resumen.retrasados}</strong> retrasados según los días de obra transcurridos
            {filtroNivel && (
              <button className="cg-desglose-limpiar" onClick={() => setFiltroNivel(null)}>
                Ver todos
              </button>
            )}
          </div>
          <div className="cg-desglose-cards">
            {desgloseRetrasados.map(d => {
              const activa = filtroNivel === d.nivel;
              return (
                <button
                  key={d.nivel}
                  type="button"
                  className={`cg-sem-card cg-sem-${d.nivel}${activa ? ' cg-sem-activa' : ''}`}
                  onClick={() => setFiltroNivel(activa ? null : d.nivel)}
                  title={activa ? 'Quitar este filtro' : `Ver solo los de ${d.label}`}
                >
                  <span className="cg-sem-punto" />
                  <span className="cg-sem-info">
                    <span className="cg-sem-label">{d.label}</span>
                    <span className="cg-sem-detalle">{d.detalle}</span>
                  </span>
                  <span className="cg-sem-cifras">
                    <span className="cg-sem-num">{d.n}</span>
                    <span className="cg-sem-pct">{d.pct}%</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="cg-filtros">
        <input
          type="text"
          className="cg-busqueda"
          placeholder="Buscar por proyecto, cliente, apartamento o residente..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <select
          className={`cg-filtro-residente${filtroResidente ? ' activo' : ''}`}
          value={filtroResidente}
          onChange={(e) => setFiltroResidente(e.target.value)}
          title="Filtrar por residente asignado"
        >
          <option value="">Todos los residentes</option>
          {residentes.map(r => <option key={r} value={r}>{r}</option>)}
          {haySinResidente && <option value={SIN_RESIDENTE}>Sin residente asignado</option>}
        </select>
        <div className="cg-filtro-estados">
          {['todos', 'en_progreso', 'retrasado', 'no_iniciado', 'completado'].map(est => (
            <button
              key={est}
              className={`cg-filtro-btn ${filtroEstado === est ? 'activo' : ''}`}
              onClick={() => setFiltroEstado(est)}
            >
              {est === 'todos' ? 'Todos' : getEstadoConfig(est).label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista de proyectos */}
      {proyectosFiltrados.length === 0 ? (
        <div className="cg-empty">
          <p>No se encontraron proyectos con cronograma</p>
        </div>
      ) : (
        <div className="cg-proyectos-list">
          {proyectosFiltrados.map(proy => {
            const estadoConf = getEstadoConfig(proy.estado_cronograma);
            const expandido = vistaExpandida === proy.proyecto_id;

            return (
              <div key={proy.proyecto_id} className={`cg-proyecto-card ${expandido ? 'expandido' : ''}`}>
                {/* Card header */}
                <div className="cg-card-header" onClick={() => setVistaExpandida(expandido ? null : proy.proyecto_id)}>
                  <div className="cg-card-info">
                    <div className="cg-card-titulo-row">
                      <span className="cg-card-icon">{estadoConf.icon}</span>
                      <h3 className="cg-card-titulo">{proy.proyecto_nombre}</h3>
                      <span
                        className="cg-estado-badge"
                        style={{ color: estadoConf.color, backgroundColor: estadoConf.bg }}
                      >
                        {estadoConf.label}
                      </span>
                    </div>
                    <div className="cg-card-meta">
                      <span className="cg-meta-item">
                        <strong>Cliente:</strong> {proy.cliente}
                      </span>
                      {proy.apartamento && (
                        <span className="cg-meta-item">
                          <strong>Apto:</strong> {proy.apartamento}
                        </span>
                      )}
                      <span className={`cg-meta-item${proy.residente ? '' : ' cg-sin-residente'}`}>
                        <strong>Residente:</strong> {proy.residente || 'Sin asignar'}
                      </span>
                      <span className="cg-meta-item">
                        <strong>Fechas:</strong> {formatFecha(proy.fecha_inicio)} → {formatFecha(proy.fecha_fin)}
                      </span>
                      {proy.dias_restantes !== 0 && proy.estado_cronograma !== 'completado' && (
                        <span className={`cg-meta-item ${proy.dias_restantes < 0 ? 'cg-alerta' : ''}`}>
                          {proy.dias_restantes > 0 ? `${proy.dias_restantes} días restantes` : `${Math.abs(proy.dias_restantes)} días de retraso`}
                        </span>
                      )}
                      {(() => {
                        // Semáforo por días transcurridos desde el inicio del cronograma
                        const sem = semaforoCronograma(proy.fecha_inicio);
                        if (!sem) return null;
                        return (
                          <span
                            className="cg-semaforo"
                            style={{ color: sem.color, background: sem.bg, borderColor: sem.border }}
                            title={`${sem.dias} días desde el inicio del cronograma · ${sem.label} (45 = amarillo, 60 = rojo)`}
                          >
                            ● {sem.dias} días de obra
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="cg-card-progreso">
                    <div className="cg-progreso-circular">
                      <svg viewBox="0 0 36 36">
                        <path
                          className="cg-circle-bg"
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                        <path
                          className="cg-circle-fg"
                          strokeDasharray={`${proy.progreso}, 100`}
                          style={{ stroke: estadoConf.color }}
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                      </svg>
                      <span className="cg-progreso-texto">{proy.progreso}%</span>
                    </div>
                    <div className="cg-card-stats-mini">
                      <span className="cg-stat-completadas">{proy.completadas}</span>
                      <span className="cg-stat-sep">/</span>
                      <span className="cg-stat-total">{proy.total_actividades}</span>
                    </div>
                    <span className="cg-expand-icon">{expandido ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Actividad actual */}
                {!expandido && proy.actividad_actual && (
                  <div className="cg-actividad-actual">
                    <span className="cg-actividad-label">Actividad actual:</span>
                    <span className="cg-actividad-nombre">{proy.actividad_actual.nombre}</span>
                    <span className="cg-actividad-fechas">
                      {formatFecha(proy.actividad_actual.fecha_inicio)} - {formatFecha(proy.actividad_actual.fecha_fin)}
                    </span>
                  </div>
                )}

                {/* Timeline expandido */}
                {expandido && (
                  <div className="cg-timeline-wrapper">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                      <button className="cron-btn-evidencias" style={{ background: '#4a7c59', color: '#fff', border: 'none', padding: '7px 14px', fontSize: '0.82rem' }}
                        onClick={() => setInformeProyectoId(proy.proyecto_id)}>
                        📄 Generar Informe de Avance
                      </button>
                    </div>
                    {/* Barra de progreso general */}
                    <div className="cg-progress-bar-container">
                      <div className="cg-progress-bar">
                        <div
                          className="cg-progress-fill"
                          style={{ width: `${proy.progreso}%`, backgroundColor: estadoConf.color }}
                        ></div>
                      </div>
                      <div className="cg-progress-labels">
                        <span>{formatFecha(proy.fecha_inicio)}</span>
                        <span>{formatFecha(proy.fecha_fin)}</span>
                      </div>
                    </div>

                    {/* Resumen de seguimiento */}
                    <div className="cg-seguimiento-resumen">
                      <span className="cg-seg-item cg-seg-completadas">✅ {proy.completadas} completadas</span>
                      <span className="cg-seg-item cg-seg-progreso">🔵 {proy.en_progreso} en progreso</span>
                      <span className="cg-seg-item cg-seg-retrasadas">🔴 {proy.retrasadas || 0} retrasadas</span>
                      <span className="cg-seg-item cg-seg-pendientes">⏳ {proy.pendientes} pendientes</span>
                    </div>

                    {/* Tabla comparativa con fechas reales editables */}
                    <div className="cg-tabla-comparativa">
                      <table>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Actividad</th>
                            <th>Inicio Plan</th>
                            <th>Fin Plan</th>
                            <th>Inicio Real</th>
                            <th>Fin Real</th>
                            <th>Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {proy.actividades.map(act => {
                            const actEstado = act.fecha_fin_real ? 'completado' : act.fecha_inicio_real ? 'en_progreso' : 'pendiente';
                            return (
                              <tr key={act.id} className={`cg-row-${actEstado}`}>
                                <td>{act.orden}</td>
                                <td className="cg-act-nombre">{act.es_hito ? '◆ ' : ''}{act.nombre}</td>
                                <td className="cg-fecha-plan">{formatFecha(act.fecha_inicio)}</td>
                                <td className="cg-fecha-plan">{!act.es_hito ? formatFecha(act.fecha_fin) : '—'}</td>
                                <td className="cg-fecha-real">
                                  {!act.es_hito ? (
                                    <input type="date" className="cg-date-input"
                                      key={`ini-${act.id}-${act.fecha_inicio_real || ''}`}
                                      defaultValue={act.fecha_inicio_real || ''}
                                      onChange={async (e) => {
                                        try {
                                          await client.mutate(`mutation($id: Int!, $f: String) { guardarFechaRealActividad(actividad_id: $id, fecha_inicio_real: $f) { success } }`,
                                            { id: act.id, f: e.target.value || null });
                                        } catch {}
                                      }}
                                    />
                                  ) : '—'}
                                </td>
                                <td className="cg-fecha-real">
                                  {!act.es_hito ? (
                                    <input type="date" className="cg-date-input"
                                      key={`fin-${act.id}-${act.fecha_fin_real || ''}`}
                                      defaultValue={act.fecha_fin_real || ''}
                                      onChange={async (e) => {
                                        try {
                                          await client.mutate(`mutation($id: Int!, $f: String) { guardarFechaRealActividad(actividad_id: $id, fecha_fin_real: $f) { success } }`,
                                            { id: act.id, f: e.target.value || null });
                                        } catch {}
                                      }}
                                    />
                                  ) : '—'}
                                </td>
                                <td>
                                  <span className={`cg-estado-mini cg-estado-mini-${actEstado}`}>
                                    {actEstado === 'completado' ? '✅' : actEstado === 'en_progreso' ? '🔵' : '⏳'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Checklist de subtareas por semana (sincrónico con vista del proyecto) */}
                    {proy.actividades.some(a => (a.tareas || []).length > 0) && (
                      <div className="cg-subtareas-section">
                        <h4 className="cg-subtareas-title">Checklist de actividades por semana</h4>
                        <p className="cg-subtareas-hint">
                          Marca cada subtarea como completada (✓) o no aplica (N/A). La fecha y el usuario quedan registrados.
                          Los cambios se sincronizan automáticamente con la vista del proyecto.
                        </p>
                        <div className="cg-subtareas-grid">
                          {proy.actividades.filter(a => (a.tareas || []).length > 0).map(act => {
                            const total = act.tareas.length;
                            const hechas = act.tareas.filter(t => t.estado === 'completado').length;
                            const naCount = act.tareas.filter(t => t.estado === 'na').length;
                            const pct = total > 0 ? Math.round(((hechas + naCount) / total) * 100) : 0;
                            return (
                              <div key={act.id} className="cg-semana-card">
                                <div className="cg-semana-card-header">
                                  <div>
                                    <NombreSemanaEditable item={act} onRenombrada={recargarTrasRenombrar} />
                                    <div className="cg-semana-meta">
                                      {act.fecha_inicio && `📅 ${formatFecha(act.fecha_inicio)}`}
                                      {act.duracion_dias > 0 && ` · ${act.duracion_dias} días`}
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                    <button type="button" className="cron-btn-evidencias" title="Evidencias (fotos/documentos)" onClick={() => setEvidenciaActividad(act)}>📎 Evidencias</button>
                                    <div className="cg-semana-progress" title={`${hechas} completadas · ${naCount} N/A · ${total} subtareas`}>
                                      <div className="cg-semana-progress-bar">
                                        <div style={{ width: `${act.progreso ?? pct}%`, background: (act.progreso ?? pct) >= 100 ? '#10b981' : '#3b82f6' }} />
                                      </div>
                                      <span>{act.progreso ?? pct}%</span>
                                    </div>
                                  </div>
                                </div>
                                <ul className="cg-tareas-list">
                                  {act.tareas.map(t => {
                                    const completado = t.estado === 'completado';
                                    const esNA = t.estado === 'na';
                                    const colorTipo = t.tipo === 'revision_coordinadora' ? '#f59e0b'
                                      : t.tipo === 'pago' ? '#10b981' : '#64748b';
                                    return (
                                      <li key={t.id} className={`cg-tarea-item ${completado ? 'completado' : ''} ${esNA ? 'na' : ''}`}>
                                        <div className="cg-tarea-main">
                                          <label className="cg-tarea-check">
                                            <input
                                              type="checkbox"
                                              checked={completado}
                                              onChange={(e) => handleMarcarTarea(t.id, e.target.checked ? 'completado' : 'pendiente')}
                                            />
                                            <span className="cg-tarea-desc" style={{ color: colorTipo }}>
                                              {t.tipo === 'revision_coordinadora' && '🟤 '}
                                              {t.tipo === 'pago' && '💰 '}
                                              {t.descripcion}
                                            </span>
                                          </label>
                                          {!esNA && t.tipo !== 'revision_coordinadora' && (
                                            <span className="cron-subtarea-pct">
                                              <input type="number" min="0" max="100"
                                                key={`sp-${t.id}-${completado ? 100 : (t.progreso ?? 0)}`}
                                                defaultValue={completado ? 100 : (t.progreso ?? 0)}
                                                onClick={(e) => e.stopPropagation()}
                                                onBlur={async (e) => {
                                                  const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                                                  try {
                                                    await client.mutate(`mutation($id: Int!, $p: Int!) { actualizarProgresoSubtarea(id: $id, progreso: $p) { success } }`, { id: t.id, p: v });
                                                    cargarDatos();
                                                  } catch {}
                                                }}
                                              />%
                                            </span>
                                          )}
                                          <button
                                            type="button"
                                            className={`cg-btn-na ${esNA ? 'active' : ''}`}
                                            onClick={() => handleMarcarTarea(t.id, esNA ? 'pendiente' : 'na')}
                                            title={esNA ? 'Quitar N/A' : 'Marcar como No Aplica'}
                                          >
                                            {esNA ? '✓ N/A' : 'N/A'}
                                          </button>
                                        </div>
                                        {completado && (
                                          <div className="cg-tarea-meta">
                                            ✓ {t.fecha_completado ? new Date(t.fecha_completado).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                            {t.usuario_nombre && ` · por ${t.usuario_nombre}`}
                                          </div>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="cg-timeline-actions">
                      <button
                        className="cg-btn cg-btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onVerDetalle) onVerDetalle(proy.proyecto_id);
                        }}
                      >
                        Ver proyecto completo
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ModalEvidenciasCronograma
        show={!!evidenciaActividad}
        actividad={evidenciaActividad}
        onClose={() => setEvidenciaActividad(null)}
      />
      <ModalInformeAvance
        show={!!informeProyectoId}
        proyectoId={informeProyectoId}
        onClose={() => setInformeProyectoId(null)}
      />
    </div>
  );
};

export default CronogramaGeneral;
